'use client';

import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { CircularProgress } from '@jouzen/ecom-components/v3/CircularProgress';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import type { FinishSelectionData } from '@jouzen/ecom-components/v3/FinishRadioSelectors';
import { FinishSwatch } from '@jouzen/ecom-components/v3/FinishSwatch';
import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { Modal } from '@jouzen/ecom-components/v4/Modal';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import { getRingFinishesData } from '@/app/[locale]/store/rings/_utils/getRingFinishesData';
import {
  OR4_JADE_POP_PATHNAME,
  STORE_POP_PATHNAME,
} from '@/app/consts/pathnames';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';
import { useProductsData } from '@/queries/Products';
import getProductListPriceRange from '@/utils/getProductListPriceRange';

import Image from '../Image';
import RichText from '../RichText';
import CompareModelsTable from './CompareModelsTable';
import CompareModelsTableCell from './CompareModelsTableCell';
import CompareModelsTableRow from './CompareModelsTableRow';
import type { CompareModelsModalVariant } from './types';

export interface CompareJadeOR4ModalProps {
  readonly or4SwatchData: FinishSelectionData[];
  readonly jadeCeramicSwatchData: FinishSelectionData[];
  readonly onTriggerButtonClick: () => void;
}

const VARIANT: CompareModelsModalVariant = {
  md: 'three_column',
  lg: 'three_column',
};

const CompareJadeOR4ModalContent = ({
  or4SwatchData,
  jadeCeramicSwatchData,
  productsData,
}: Pick<CompareJadeOR4ModalProps, 'or4SwatchData' | 'jadeCeramicSwatchData'> & {
  readonly productsData: ListProduct[] | undefined;
}) => {
  const t = useTranslations();
  const { currencyCode } = useCurrency();
  const isLargeScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.large}px)`);

  const or4ProductData = getRingFinishesData(
    or4SwatchData,
    'or4',
    productsData,
  );
  const jadeCeramicProductData = getRingFinishesData(
    jadeCeramicSwatchData,
    'or4-cer',
    productsData,
  );

  const or4PriceRange = getProductListPriceRange(
    or4ProductData.map(({ price }) => ({
      basePrice: price,
    })),
  );
  const or4CeramicPriceRange = getProductListPriceRange(
    jadeCeramicProductData.map(({ price }) => ({
      basePrice: price,
    })),
  );

  const handleClickLearnMore = (path: string) => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'learn more',
      location: 'modal',
      path,
      action: 'go to pop',
    });
  };

  return (
    <CompareModelsTable tableBodyClassName="gap-y-2!">
      <CompareModelsTableRow variant={VARIANT}>
        <CompareModelsTableCell variant={VARIANT} className="flex flex-col">
          <div className="flex flex-1 flex-col items-center gap-4">
            <Image
              localizedAlt="jade_midnight_product_img_alt"
              src="jade-pop/midnight-front-view"
              format="png"
              width={96}
              height={96}
            />
            <Typography className="text-center text-heading-xs">
              <RichText messageKey="oura_ring_4_ceramic" />
            </Typography>
            <div className="flex flex-1 items-end gap-1 lg:gap-2">
              {jadeCeramicProductData.map(({ id, ...swatchProps }) => (
                <FinishSwatch
                  key={id}
                  {...swatchProps}
                  borderClassName="cursor-default"
                  size={isLargeScreen ? 'small' : 'extra-small'}
                />
              ))}
            </div>
          </div>
        </CompareModelsTableCell>
        <CompareModelsTableCell variant={VARIANT} className="flex flex-col">
          <div className="flex flex-1 flex-col items-center gap-4">
            <Image
              localizedAlt="or4_silver_product_img_alt"
              src="product/images/or4-silver-tilt-source"
              format="png"
              width={96}
              height={96}
            />
            <Typography className="text-center text-heading-xs">
              <RichText messageKey="oura_ring_4" />
            </Typography>
            <div className="flex flex-1 items-end gap-1 lg:gap-2">
              {or4ProductData.map(({ id, ...swatchProps }) => (
                <FinishSwatch
                  key={id}
                  {...swatchProps}
                  borderClassName="cursor-default"
                  size={isLargeScreen ? 'small' : 'extra-small'}
                />
              ))}
            </div>
          </div>
        </CompareModelsTableCell>
      </CompareModelsTableRow>
      {or4PriceRange && or4CeramicPriceRange && (
        <CompareModelsTableRow
          header="compare_models_modal_price_th"
          variant={VARIANT}
          headerClassName="[&]:lg:text-base"
        >
          <CompareModelsTableCell
            className="[&]:md:text-base [&]:lg:text-base"
            variant={VARIANT}
          >
            <Price
              currencyCode={currencyCode}
              price={or4CeramicPriceRange.minPrice}
            />
            {or4CeramicPriceRange.maxPrice !==
              or4CeramicPriceRange.minPrice && (
              <>
                <span> - </span>
                <Price
                  currencyCode={currencyCode}
                  price={or4CeramicPriceRange.maxPrice}
                />
              </>
            )}
          </CompareModelsTableCell>
          <CompareModelsTableCell
            className="[&]:md:text-base [&]:lg:text-base"
            variant={VARIANT}
          >
            <Price currencyCode={currencyCode} price={or4PriceRange.minPrice} />
            {or4PriceRange.maxPrice !== or4PriceRange.minPrice && (
              <>
                <span> - </span>
                <Price
                  currencyCode={currencyCode}
                  price={or4PriceRange.maxPrice}
                />
              </>
            )}
          </CompareModelsTableCell>
        </CompareModelsTableRow>
      )}
      <CompareModelsTableRow
        header="compare_models_modal_design_th_v2"
        variant={VARIANT}
      >
        <CompareModelsTableCell
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText messageKey="compare_models_modal_design_jade" />
        </CompareModelsTableCell>
        <CompareModelsTableCell
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText messageKey="compare_models_modal_design_or4_v2" />
        </CompareModelsTableCell>
      </CompareModelsTableRow>
      <CompareModelsTableRow
        header="compare_models_modal_battery_th"
        variant={VARIANT}
      >
        <CompareModelsTableCell
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText
            messageKey="compare_models_modal_battery_jade_or4"
            tValues={{
              footnoteLink: () => <sup>*</sup>,
            }}
          />
        </CompareModelsTableCell>
        <CompareModelsTableCell
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText
            messageKey="compare_models_modal_battery_jade_or4"
            tValues={{
              footnoteLink: () => <sup>*</sup>,
            }}
          />
        </CompareModelsTableCell>
      </CompareModelsTableRow>
      <CompareModelsTableRow
        header="compare_models_modal_sizing_th"
        className="[&]:border-0"
        headerClassName="[&]:border-0 [&]:md:pb-4"
        variant={VARIANT}
      >
        <CompareModelsTableCell
          className="[&]:border-0 [&]:pb-4 [&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText messageKey="compare_models_modal_sizing_or4" />
        </CompareModelsTableCell>
        <CompareModelsTableCell
          className="[&]:border-0 [&]:pb-4 [&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <RichText messageKey="compare_models_modal_sizing_or4" />
        </CompareModelsTableCell>
      </CompareModelsTableRow>
      <CompareModelsTableRow isLastRow className="gap-y-0!" variant={VARIANT}>
        <CompareModelsTableCell
          isLastRow
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <Button
            variant="outlined-dark"
            size="large"
            className="font-bold"
            href={OR4_JADE_POP_PATHNAME}
            onClick={() => handleClickLearnMore(OR4_JADE_POP_PATHNAME)}
          >
            {t('learn_more')}
          </Button>
        </CompareModelsTableCell>
        <CompareModelsTableCell
          isLastRow
          className="[&]:md:text-base [&]:lg:text-base"
          variant={VARIANT}
        >
          <Button
            variant="outlined-dark"
            size="large"
            className="font-bold"
            href={STORE_POP_PATHNAME}
            onClick={() => handleClickLearnMore(STORE_POP_PATHNAME)}
          >
            {t('learn_more')}
          </Button>
        </CompareModelsTableCell>
      </CompareModelsTableRow>
    </CompareModelsTable>
  );
};

const CompareJadeOR4Modal = ({
  or4SwatchData,
  jadeCeramicSwatchData,
  onTriggerButtonClick,
}: CompareJadeOR4ModalProps) => {
  const t = useTranslations();
  const { isLoading: isProductsQueryLoading, data: productsData } =
    useProductsData({
      includeInventory: false,
    });

  return (
    <Modal
      className="bg-gray-100"
      onTriggerButtonClick={onTriggerButtonClick}
      triggerButton={(props) => (
        <Button
          {...props}
          data-cy="compare-jade-or4-modal-trigger"
          variant="primary"
          size="large"
          className="relative mt-4 font-bold lg:mt-6"
        >
          {t('compare_rings_cta')}
        </Button>
      )}
      variant="large"
    >
      {({ titleProps }) => (
        <div
          data-cy="compare-jade-or4-modal-wrapper"
          className="text-sandstone-500"
        >
          <Typography
            className="pb-4 text-xl font-medium md:pb-0"
            Element="h2"
            id={titleProps.id!}
          >
            {t('compare_models_modal_title_v2')}
          </Typography>
          {isProductsQueryLoading ? (
            <div className="flex min-h-[150px] items-center justify-center">
              <CircularProgress data-cy="compare-jade-or4-modal-loader" />
            </div>
          ) : (
            <CompareJadeOR4ModalContent
              or4SwatchData={or4SwatchData}
              jadeCeramicSwatchData={jadeCeramicSwatchData}
              productsData={productsData}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default CompareJadeOR4Modal;
